// components/ClientLayout.jsx
'use client';

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { MobileMenuProvider } from '@/contexts/MobileMenuContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import AuthGuard from '@/components/AuthGuard';
import Header from '@/components/Header';
import Sidebar from '@/components/Sidebar';
import HouseSidebar from '@/components/HouseSidebar';
import PortalHouseSidebar from '@/components/PortalHouseSidebar';
import { usePathname } from 'next/navigation';
import { isAccountType, isOrgAccountType } from '@/utils/builtInApplets';

// Picks the right house sidebar based on the current account. Must live inside
// AuthProvider so it can read currentOrg.
function LayoutShell({ children }) {
    const pathname = usePathname();
    const { currentOrg } = useAuth();

    // Pages that don't show header or sidebar (public pages)
    const publicPaths = ['/login', '/reset-password'];
    const isPublicPath = publicPaths.some(path => pathname.startsWith(path));

    // Pages that show header but NOT sidebar (full-width authenticated pages).
    // '/addorg' is reached from /memberships and may run before any org is
    // selected, so it gets the same chrome-less treatment.
    //
    // ...unless there is NO current org. Legacy's chrome for that state
    // (NoOrgNavMenu.razor L34-44, and NavMenu.razor's own
    // `Auth.CurrentOrg != null` gates) is a sidebar of exactly Home + My
    // Account, with Logout in the header — and buildPortalNavItems already
    // returns precisely that pair when `currentOrg` is null. Hiding the
    // sidebar here regardless left a user with zero organizations no route to
    // My Account at all (audit 12t item 3). So the chrome-less picker is kept
    // for a signed-in user switching orgs, and the minimal legacy sidebar
    // appears when nothing is selected.
    const noSidebarPaths = ['/memberships', '/addorg'];
    const chromelessPicker = !!currentOrg && noSidebarPaths.some(path => pathname.startsWith(path));
    // A shared training-cal login gets the same chrome-less shell for EVERY
    // route: legacy TrainingCalShared.razor uses `@layout TrainingCalLayout`
    // (Shared/TrainingCalLayout.razor) — the FSE logo and a Logout button,
    // no nav menu at all. Same mechanism as the SharedHouse reduced menu
    // (account-type test on currentOrg), applied one level up because there
    // is no menu to reduce. buildPortalNavItems returns [] for this type too,
    // so the dashboard shortcut picker agrees. Audit 12h, OUTSTANDING §C57.
    const isSharedTrainingCalAccount = isAccountType(currentOrg, 'SharedTrainingCal');
    const hideSidebar = isSharedTrainingCalAccount || chromelessPicker;

    // Print views: authenticated, but no header and no sidebar so the page
    // prints clean (the Blazor equivalent was PrintLayout).
    const printPaths = ['/analyticsprint'];
    const isPrintPath = printPaths.some(path => pathname.startsWith(path));

    // House routes (either sidebar). Includes the house-only top-level pages
    // (subscriptions / help center) so they keep the house chrome too.
    const isHousePath =
        pathname === '/houseadmin' ||
        pathname.startsWith('/houseadmin/') ||
        pathname.startsWith('/house/') ||
        pathname === '/househome' ||
        pathname === '/housesubscriptions' ||
        pathname === '/helpcenterhouse';

    // TWO different enums decide this and they are easy to confuse: HouseOnly is
    // a property of the ORG, SharedHouse of the ACCOUNT. Both describe someone
    // who lives entirely inside one house, so both get the house-only chrome.
    // A portal user who drilled into a house from the house list gets the portal
    // sidebar instead (Back + House Admin) — for them that list is a real place
    // to return to.
    //
    // Testing the org type alone left shared station logins with
    // PortalHouseSidebar, whose "Back" points at the house list — a page that
    // immediately redirects those accounts straight back to where they were.
    // Owner direction 2026-08-08: a shared house login should see the same card
    // board a HouseOnly account gets.
    const isHouseOnlyAccount =
        isOrgAccountType(currentOrg, 'HouseOnly')
        || isAccountType(currentOrg, 'SharedHouse');

    function renderSidebar() {
        if (!isHousePath) return <Sidebar />;
        return isHouseOnlyAccount ? <HouseSidebar /> : <PortalHouseSidebar />;
    }

    return (
        <>
            {!isPublicPath && !isPrintPath && <Header showMenuButton={!hideSidebar} />}
            {!isPublicPath && !isPrintPath && !hideSidebar && renderSidebar()}
            <main
                className={`
                    min-h-screen
                    ${!isPublicPath && !isPrintPath ? 'pt-with-header' : ''}
                    ${!isPublicPath && !isPrintPath && !hideSidebar ? 'lg:ml-[var(--sidebar-width)]' : ''}
                    transition-[margin] duration-300 ease-in-out
                `}
            >
                {children}
            </main>
        </>
    );
}

export default function ClientLayout({ children }) {
    return (
        <ThemeProvider>
            <AuthProvider>
                <MobileMenuProvider>
                    <AuthGuard>
                        <LayoutShell>{children}</LayoutShell>
                    </AuthGuard>
                </MobileMenuProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}